//libraries
import React, { Suspense } from 'react';
import styled from 'styled-components';

// styled components
import { Frame, PrimaryButton, Flex, ColumnedFlex } from 'shared/styledComponents';
import { H1, Text, Title } from 'shared/typography';
import colors from 'theme/colors';
import devices from 'theme/devices';

// custom components
import Layout from 'components/Layout';
import Icon from 'components/Icon';
import BannerAd from './BannerAd';

import ParentbirthContentAd from 'components/ParentbirthContentAd';

const RealStoryPreview = React.lazy(() => import('pages/RealStory/components/RealStoriesPreview'));
const RelatedArticlesOnServicePage = React.lazy(() => import('pages/Articles/components/RelatedArticlesOnServicePage'));

const MainSectionWrapper = styled(Flex)`
  background-color: ${({ theme: { colors } }): string => colors.secondary};
`;

const MainSection = styled(Frame)`
  position: relative;
  flex-direction: column;
  padding-bottom: 155px;
  padding-top: 80px;

  @media ${devices.mobile} {
    padding-top: 0;
    padding-bottom: 250px;
  }
`;

const MainSectionTitle = styled(H1)`
  max-width: 600px;
  margin-bottom: 25px;
`;

const MainSectionSubtitle = styled(Title)`
  max-width: 540px;
  font-size: 18px;
  line-height: 30px;

  @media ${devices.mobile} {
    max-width: 280px;
  }
`;

const FindSpecialistButton = styled(PrimaryButton)(
  ({ isAtTheBottom }) => `
    max-width: 225px;
    margin-top: 20px;
    padding: 19px 20px;

    ${isAtTheBottom && 'margin: auto'};
    ${isAtTheBottom && 'margin-bottom: 0'};
    ${isAtTheBottom && 'display: flex'};

    @media ${devices.mobile} {
      margin-top: ${isAtTheBottom ? '50px' : '35px'};
    }
  `,
);

const InformationSection = styled.div`
  position: relative;
  width: 100%;
  min-height: 500px;
`;

const ServiceIcon = styled.img<any>`
  position: absolute;
  top: -65px;
  left: 350px;
  width: 177px;
  height: 177px;

  @media ${devices.mobile} {
    top: -215px;
    left: 20px;
    width: 140px;
    height: 140px;
    z-index: 2;
  }
`;

const ServiceImage = styled.div<any>`
  position: absolute;
  top: 65px;
  right: 0;
  width: 464px;
  height: 512px;
  background-image: ${({ src }): string => `url(${src})`};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  @media ${devices.mobile} {
    top: 375px;
    right: 10px;
    width: 177px;
    height: 196px;
  }
`;

const InfoBlock = styled(Flex)`
  margin-bottom: 125px;
  padding-top: 150px;

  @media ${devices.mobile} {
    flex-direction: column;
    margin-bottom: 70px;
    padding-top: 50px;
  }
`;

const SubTitles = styled(ColumnedFlex)`
  max-width: 195px;
  margin-right: 70px;

  @media ${devices.mobile} {
    flex-direction: row;
    max-width: 310px;
    margin-right: 0;
    overflow-x: auto;
  }
`;

const StyledArrowIcon = styled(Icon)`
  align-self: center;
  align-items: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const StyledLeftIcon = styled(StyledArrowIcon)`
  margin-right: 10px;
  transform: rotate(90deg);
`;

const StyledRightIcon = styled(StyledArrowIcon)`
  margin-left: 10px;
  transform: rotate(-90deg);
`;

const SubtitleTextWrapper = styled(ColumnedFlex)`
  gap: 2rem;
`;

const SubtitleText = styled(Text)`
  max-width: 705px;
  font-size: 14px;
  line-height: 26px;
  color: ${colors.lightBrown};

  @media ${devices.mobile} {
    line-height: 22px;
  }
`;

const InfoBlockSubtitle = styled(Title)<any>(
  ({ isSelected }) => `
    width: 100%;
    padding: 30px 0;
    border-bottom: 1px solid ${colors.alto};
    cursor: pointer;
    opacity: ${isSelected ? '0.6' : '1'};
    text-decoration: underline;

    :last-of-type {
      border-bottom: 0;
    }

    @media ${devices.mobile} {
      padding: 15px 0;
      border-bottom: 0;
      min-width: 260px;
      text-align: center;
    }
    
  `,
);

const InfoBlockSubtitleH2 = styled.h2`
  font-size: inherit;
  font-weight: inherit;
`;

const RelatedArticlesTitle = styled(Title)`
  width: 100%;
  padding-bottom: 25px;
  font-size: 14px;
  font-family: ${({ theme: { fonts } }): string => fonts.wigrumRegular};
  font-weight: bold;
  border-bottom: 1px solid ${colors.alto};
`;

const RelatedArticlesBlock = styled(Flex)`
  justify-content: space-between;
  margin-top: 25px;

  @media ${devices.mobile} {
    flex-direction: column;
  }
`;

const ArticlesWrapper = styled(ColumnedFlex)`
  margin-bottom: 160px;

  @media ${devices.mobile} {
    margin-bottom: 0;
  }
`;

const StoryWrapper = styled(Flex)`
  margin-top: 140px;
  margin-bottom: 160px;

  @media ${devices.mobile} {
    margin-top: 75px;
  }
`;

const SubTitlesWrapper = styled(Flex)`
  @media ${devices.mobile} {
    margin-bottom: 40px;
  }
`;

interface ServiceProps {
  selectedSubtitle: any;
  handleSelectSubtitle: (subtitle: any) => any;
  currentServiceName: string;
  handleRedirectToFindProvidersPage: (value: string) => void;
  serviceAssets: any;
  content: any;
  handlLeftArrowClick: any;
  handleRightArrowClick: any;
  stories: any;
  articles: any;
  isDesktop: boolean;
  onStoryClick: (id: string) => () => void;
  onArticleClick: (id: string) => () => void;
}

const Service = ({
  selectedSubtitle,
  handleSelectSubtitle,
  currentServiceName,
  serviceAssets,
  handleRedirectToFindProvidersPage,
  content,
  stories,
  handleRightArrowClick,
  handlLeftArrowClick,
  articles,
  onStoryClick,
  onArticleClick,
  isDesktop,
}: ServiceProps): JSX.Element => (
  <Layout>
    <ColumnedFlex>
      <MainSectionWrapper>
        <MainSection>
          <ColumnedFlex>
            <MainSectionTitle>{content.h1}</MainSectionTitle>
            <MainSectionSubtitle>{content.text}</MainSectionSubtitle>
            <FindSpecialistButton
              as="button"
              size="small"
              onClick={() => handleRedirectToFindProvidersPage(currentServiceName)}
            >
              find your specialist
            </FindSpecialistButton>
          </ColumnedFlex>
          <ServiceImage src={serviceAssets.image} />
        </MainSection>
      </MainSectionWrapper>
      <Frame>
        <InformationSection>
          <ServiceIcon src={serviceAssets.icon} alt={currentServiceName} />
          <InfoBlock>
            <SubTitlesWrapper>
              {!isDesktop && content.subtitles?.length > 1 && (
                <StyledLeftIcon type="downArrow" width="18px" height="13px" onClick={handlLeftArrowClick} />
              )}

              <SubTitles id="subtitles">
                {content.subtitles.map((subtitle: any) => (
                  <InfoBlockSubtitle
                    as={'a'}
                    id="subtitleLink"
                    href={`#${subtitle.title.split(' ').join('-').toLowerCase()}`}
                    size="small"
                    key={subtitle.id}
                    onClick={handleSelectSubtitle(subtitle)}
                    isSelected={subtitle.id === selectedSubtitle.id}
                  >
                    {subtitle.asH2 ? <InfoBlockSubtitleH2>{subtitle.title}</InfoBlockSubtitleH2> : subtitle.title}
                  </InfoBlockSubtitle>
                ))}
              </SubTitles>
              {!isDesktop && content.subtitles?.length > 1 && (
                <StyledRightIcon type="downArrow" width="18px" height="13px" onClick={handleRightArrowClick} />
              )}
            </SubTitlesWrapper>
            <SubtitleTextWrapper>
              {content.id === 'Coaching' ? <BannerAd /> : <ParentbirthContentAd />}
              <SubtitleText as="div">{selectedSubtitle.text()}</SubtitleText>
            </SubtitleTextWrapper>
          </InfoBlock>
          <ArticlesWrapper>
            {articles && !!articles.length && (
              <>
                <RelatedArticlesTitle size="small">Related Articles</RelatedArticlesTitle>
                <RelatedArticlesBlock>
                  <Suspense fallback={<div>Loading...</div>}>
                    <RelatedArticlesOnServicePage articles={articles} onArticleClick={onArticleClick} />
                  </Suspense>
                </RelatedArticlesBlock>
              </>
            )}
          </ArticlesWrapper>
          {stories && !!stories.length && (
            <StoryWrapper>
              <Suspense fallback={<div>Loading...</div>}>
                <RealStoryPreview stories={stories} onStoryClick={onStoryClick} />
              </Suspense>
            </StoryWrapper>
          )}
          <FindSpecialistButton as="button" size="small" isAtTheBottom onClick={handleRedirectToFindProvidersPage}>
            find your specialist
          </FindSpecialistButton>
        </InformationSection>
      </Frame>
    </ColumnedFlex>
  </Layout>
);

export default Service;
